import React, { useEffect, useMemo, useState } from 'react';
import { X, Rocket, Sparkles, Star, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { subscribeToReleaseNotes } from '../../services/articleService';
import type { Article } from '../../types';

const SEEN_KEY = 'fricamp_whatsnew_seen_at';

/** Timestamp (ms) for et versjonsnotat — tåler Firestore Timestamp, {seconds}, tall eller ISO-streng. */
function noteMillis(a: Article): number {
  const raw: any = a.published_at ?? a.created_at;
  if (!raw) return 0;
  if (typeof raw.toMillis === 'function') return raw.toMillis();
  if (typeof raw.seconds === 'number') return raw.seconds * 1000;
  const n = typeof raw === 'number' ? raw : new Date(raw).getTime();
  return isNaN(n) ? 0 : n;
}

/** Velg riktig språkversjon av et versjonsnotat (base = norsk, med en/de-oversettelser). */
function localize(a: Article, lang: string): { title: string; summary: string; content: string } {
  const key = lang?.split('-')[0];
  const tr = key === 'en' || key === 'de' ? a.translations?.[key] : undefined;
  if (tr) return { title: tr.title, summary: tr.summary, content: tr.content };
  return { title: a.title, summary: a.summary, content: a.content };
}

/**
 * Hook: antall uleste versjonsnotater (nyere enn sist brukeren åpnet «Hva er nytt»).
 * Fail-soft — returnerer 0 ved feil/tom.
 */
export function useWhatsNewUnread(): number {
  const [notes, setNotes] = useState<Article[]>([]);
  const [seenAt, setSeenAt] = useState<number>(() => {
    const v = Number(localStorage.getItem(SEEN_KEY) || 0);
    return isNaN(v) ? 0 : v;
  });

  useEffect(() => subscribeToReleaseNotes(setNotes, 20), []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SEEN_KEY) setSeenAt(Number(e.newValue || 0));
    };
    window.addEventListener('storage', onStorage);
    const onSeen = () => setSeenAt(Number(localStorage.getItem(SEEN_KEY) || 0));
    window.addEventListener('fricamp:whatsnew-seen', onSeen);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('fricamp:whatsnew-seen', onSeen);
    };
  }, []);

  return useMemo(() => notes.filter(n => noteMillis(n) > seenAt).length, [notes, seenAt]);
}

interface WhatsNewModalProps {
  onClose: () => void;
}

const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [notes, setNotes] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeToReleaseNotes((n) => { setNotes(n); setLoading(false); }, 30);
    return unsub;
  }, []);

  // Marker alt som sett når panelet åpnes.
  useEffect(() => {
    const now = Date.now();
    localStorage.setItem(SEEN_KEY, String(now));
    window.dispatchEvent(new Event('fricamp:whatsnew-seen'));
  }, []);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language || 'nb-NO', { day: 'numeric', month: 'long', year: 'numeric' }),
    [i18n.language]
  );

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[10001] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-stone-950/90 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--bg-base)] border border-[var(--border-primary)] rounded-modal shadow-3xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="p-8 border-b border-[var(--border-primary)] shrink-0 flex justify-between items-center bg-[var(--bg-input)]">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 text-emerald-500">
              <Rocket size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tighter">{t('whatsNew.title', 'Hva er nytt')}</h2>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{t('whatsNew.subtitle', 'Siste oppdateringer i Fricamp')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-surface-alt)] rounded-xl">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
          {loading ? (
            <div className="py-16 text-center text-[var(--text-muted)] text-xs font-bold uppercase tracking-widest animate-pulse">
              {t('common.loading', 'Laster …')}
            </div>
          ) : notes.length === 0 ? (
            <div className="py-16 text-center space-y-3">
              <Sparkles size={32} className="mx-auto text-[var(--text-muted)]" />
              <p className="text-[var(--text-muted)] text-xs font-bold uppercase tracking-widest">
                {t('whatsNew.empty', 'Ingen oppdateringer ennå')}
              </p>
            </div>
          ) : (
            notes.map((note) => {
              const loc = localize(note, i18n.language);
              const hero = (note.images || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0))[0];
              const ms = noteMillis(note);
              const isMajor = note.impact === 'major';
              return (
                <div key={note.id} className="rounded-panel border border-[var(--border-primary)] bg-[var(--bg-surface)] overflow-hidden">
                  {hero && (
                    <img src={hero.url} alt={hero.caption || loc.title} loading="lazy" className="w-full h-40 object-cover" />
                  )}
                  <div className="p-5 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {note.version && (
                        <span className="text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border border-[var(--border-primary)]">
                          v{note.version}
                        </span>
                      )}
                      {isMajor && (
                        <span className="text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                          <Star size={9} fill="currentColor" /> {t('whatsNew.majorBadge', 'Stor nyhet')}
                        </span>
                      )}
                      {ms > 0 && (
                        <span className="text-3xs font-bold text-[var(--text-muted)] ml-auto">{dateFmt.format(new Date(ms))}</span>
                      )}
                    </div>
                    <h3 className="text-base font-black text-[var(--text-primary)] tracking-tight leading-snug">{loc.title}</h3>
                    {loc.summary && (
                      <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{loc.summary}</p>
                    )}
                    {note.slug && (
                      <a
                        href={`https://www.fricamp.com/blogg/${note.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-2xs font-black uppercase tracking-widest text-emerald-400 hover:text-emerald-300 transition-colors pt-1"
                      >
                        {t('whatsNew.readMore', 'Les mer')} <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border-primary)] bg-[var(--bg-input)]">
          <button onClick={onClose} className="w-full bg-stone-100 text-stone-950 py-4 rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all">
            {t('whatsNew.button', 'Lukk')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
